import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
  Stack,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Grid
} from '@mui/material';
import { Visibility, VisibilityOff, Storage, Dns, Sensors, AddLink, ViewColumn, FilterList, DensitySmall, IosShare, Close, Check } from '@mui/icons-material';
import { getConnections as getDevopsConnections } from '../services/devopsAPIService';
import { configureConnection as postConfigureConnection } from '../services/devopsAPIService';
import { getConnectionJobs as fetchConnectionJobs } from '../services/devopsAPIService';
import { getAwxConnection, awxAccountSetup } from '../services/devopsAPIService';

const StatCard = ({ title, value, color = '#334B65', bg = '#EEF4FA', icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      bgcolor: bg,
      backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.0), rgba(0,0,0,0.02))',
      border: '1px solid #eef2f6',
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar variant="rounded" sx={{ bgcolor: '#E3EDF7', color: '#1B4965', width: 44, height: 44 }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="subtitle2" color="#76818B" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AddConnectionModal = ({ open, onClose, onSave }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    connectionName: '',
    infraType: 'AWS',
    connectionType: 'SSH',
    hostName: '',
    username: '',
    password: '',
    port: ''
  });
  const [pemFile, setPemFile] = useState(null);

  const isValid = useMemo(() => (
    form.connectionName.trim() !== '' &&
    form.infraType.trim() !== '' &&
    form.hostName.trim() !== '' &&
    form.username.trim() !== ''
  ), [form]);

  const handleChange = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      connectionId: Math.floor(Math.random() * 100000),
      connectionName: form.connectionName,
      connectionType: form.connectionType,
      hostName: form.hostName,
      infraType: form.infraType,
      isActive: 1,
      port: form.port || '22',
      username: form.username,
      createdBy: 'you',
      createdOn: new Date().toISOString(),
      password: form.password,
      pemFile: pemFile || undefined,
    });
  };

// AwxSetupModal is defined below, outside of AddConnectionModal

  

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 16px 48px rgba(2,6,23,0.25)' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(2,6,23,0.35)' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, letterSpacing: 0.2 }}>Add Connection</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Note: Whitelist your IP 5.223.50.175 before connection.
            </Typography>
            <TextField
              label="Connection Name"
              fullWidth
              size="small"
              value={form.connectionName}
              onChange={handleChange('connectionName')}
              placeholder="e.g., Prod Bastion SSH"
            />
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Infra Type</InputLabel>
              <Select label="Infra Type" value={form.infraType} onChange={handleChange('infraType')}>
                <MenuItem value="AWS">AWS</MenuItem>
                <MenuItem value="AZURE">Azure</MenuItem>
                <MenuItem value="DATACENTER">Data Center</MenuItem>
                <MenuItem value="GCP">GCP</MenuItem>
                <MenuItem value="ZTNA">ZTNA (VPN Tunnel)</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Port" fullWidth size="small" value={form.port} onChange={handleChange('port')} placeholder="22" />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Connection Type" fullWidth size="small" value={form.connectionType} InputProps={{ readOnly: true }} />
            <TextField label="Hostname (or IPaddress)" fullWidth size="small" value={form.hostName} onChange={handleChange('hostName')} placeholder="ec2-11-22-33-44.compute.amazonaws.com" />
          </Stack>
          <Box>
            <TextField label="Username" fullWidth size="small" value={form.username} onChange={handleChange('username')} placeholder="ec2-user" />
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Password</InputLabel>
              <OutlinedInput
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange('password')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>
          <Box>
            <Button variant="outlined" fullWidth component="label">
              {pemFile ? 'Re-upload PEM File (optional)' : 'Upload PEM File (optional)'}
              <input
                hidden
                type="file"
                accept=".pem"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  setPemFile(f);
                }}
              />
            </Button>
            {pemFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Uploaded: {pemFile.name}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isValid}
            onClick={handleSave}
            startIcon={<AddLink />}
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              py: 1,
            }}
          >
            Connect
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

// Define AWX setup modal at top-level
const AwxSetupModal = ({ open, onClose, onSetup, loading }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 16px 48px rgba(2,6,23,0.25)' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(2,6,23,0.35)' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, letterSpacing: 0.2 }}>AWX Account Setup</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          To use Setup Infrastructure, you need to link your AWX account to OpenShield. Click "Setup Now" to create your AWX account linkage.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={loading}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', py: 1 }}
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={onSetup}
            disabled={loading}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', py: 1 }}
          >
            {loading ? 'Setting up...' : 'Setup Now'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const SetupInfrastructure = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [logsState, setLogsState] = useState({}); // keyed by connectionId: { open, loading, items }
  const [activeLogs, setActiveLogs] = useState(null); // connectionId currently shown in separate panel
  const [awxLinked, setAwxLinked] = useState(null);
  const [openAwx, setOpenAwx] = useState(false);
  const [awxSubmitting, setAwxSubmitting] = useState(false);

  // Columns visibility state
  const allColumns = useMemo(() => ([
    { key: 'sno', label: 'S.no' },
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'infra', label: 'Infra' },
    { key: 'hostname', label: 'Hostname' },
    { key: 'username', label: 'Username' },
    { key: 'createdBy', label: 'Created By' },
    { key: 'status', label: 'Status' },
    { key: 'createdOn', label: 'Created On' },
    { key: 'port', label: 'Port' },
    { key: 'logs', label: 'Logs' },
  ]), []);
  const [visibleCols, setVisibleCols] = useState(() => allColumns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}));
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const [colSearch, setColSearch] = useState('');
  const openColMenu = Boolean(colMenuAnchor);
  const filteredCols = useMemo(() => allColumns.filter(c => c.label.toLowerCase().includes(colSearch.toLowerCase())), [allColumns, colSearch]);
  const toggleCol = (key) => setVisibleCols((s) => ({ ...s, [key]: !s[key] }));
  const resetCols = () => setVisibleCols(allColumns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}));
  const isVisible = (key) => !!visibleCols[key];

  // Density state
  const [density, setDensity] = useState('standard'); // 'compact' | 'standard' | 'comfortable'
  const [densityAnchor, setDensityAnchor] = useState(null);
  const openDensityMenu = Boolean(densityAnchor);

  // Filters state
  const [showFilters, setShowFilters] = useState(false);
  const filterOperators = [
    { key: 'contains', label: 'contains' },
    { key: 'not_contains', label: 'does not contain' },
    { key: 'equals', label: 'equals' },
    { key: 'not_equals', label: 'does not equal' },
    { key: 'starts_with', label: 'starts with' },
    { key: 'ends_with', label: 'ends with' },
    { key: 'is_empty', label: 'is empty' },
    { key: 'not_empty', label: 'is not empty' },
    { key: 'any_of', label: 'is any of' },
  ];
  const [filter, setFilter] = useState({ columnKey: 'sno', operator: 'contains', value: '' });

  const getCellValue = (row, key) => {
    switch (key) {
      case 'sno': return String(row.id ?? '');
      case 'name': return String(row.connectionName ?? '');
      case 'type': return String(row.connectionType ?? '');
      case 'infra': return String(row.infraType ?? '');
      case 'hostname': return String(row.hostName ?? '');
      case 'username': return String(row.username ?? '');
      case 'createdBy': return String(row.createdBy ?? '');
      case 'status': return String(Number(row.isActive) === 1 ? 'Active' : 'Inactive');
      case 'createdOn': return row.createdOn ? new Date(row.createdOn).toISOString() : '';
      case 'port': return String(row.port ?? '');
      case 'logs': return '';
      default: return '';
    }
  };

  const displayRows = useMemo(() => {
    if (!showFilters) return rows;
    const { columnKey, operator, value } = filter;
    const v = String(value ?? '').toLowerCase();
    const list = Array.isArray(rows) ? rows : [];
    return list.filter((r) => {
      const cell = String(getCellValue(r, columnKey) ?? '');
      const c = cell.toLowerCase();
      switch (operator) {
        case 'contains':
          return c.includes(v);
        case 'not_contains':
          return !c.includes(v);
        case 'equals':
          return c === v;
        case 'not_equals':
          return c !== v;
        case 'starts_with':
          return c.startsWith(v);
        case 'ends_with':
          return c.endsWith(v);
        case 'is_empty':
          return c.length === 0;
        case 'not_empty':
          return c.length > 0;
        case 'any_of': {
          const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
          if (!parts.length) return true;
          return parts.includes(c);
        }
        default:
          return true;
      }
    });
  }, [rows, showFilters, filter]);

  // Logs table: columns visibility
  const allColumnsLogs = useMemo(() => ([
    { key: 'sno', label: 'S. no' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status' },
    { key: 'type', label: 'Type' },
    { key: 'createdAt', label: 'Created On' },
    { key: 'finishedAt', label: 'Completed On' },
    { key: 'elapsedTime', label: 'Duration' },
  ]), []);
  const [visibleColsLogs, setVisibleColsLogs] = useState(() => allColumnsLogs.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}));
  const [colMenuAnchorLogs, setColMenuAnchorLogs] = useState(null);
  const [colSearchLogs, setColSearchLogs] = useState('');
  const openColMenuLogs = Boolean(colMenuAnchorLogs);
  const filteredColsLogs = useMemo(() => allColumnsLogs.filter(c => c.label.toLowerCase().includes(colSearchLogs.toLowerCase())), [allColumnsLogs, colSearchLogs]);
  const toggleColLogs = (key) => setVisibleColsLogs((s) => ({ ...s, [key]: !s[key] }));
  const resetColsLogs = () => setVisibleColsLogs(allColumnsLogs.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}));
  const isVisibleLogs = (key) => !!visibleColsLogs[key];

  // Logs table: filters
  const [showFiltersLogs, setShowFiltersLogs] = useState(false);
  const [filterLogs, setFilterLogs] = useState({ columnKey: 'name', operator: 'contains', value: '' });
  const getCellValueLogs = (log, key, index) => {
    switch (key) {
      case 'sno': return String((index ?? 0) + 1);
      case 'name': return String(log.name ?? '');
      case 'description': return String(log.description ?? '');
      case 'status': return String(log.status ?? '');
      case 'type': return String(log.type ?? '');
      case 'createdAt': return log.createdAt ? new Date(log.createdAt).toISOString().replace('T',' ').replace(/\.\d{6}Z?$/, '') : '';
      case 'finishedAt': return log.finishedAt ? new Date(log.finishedAt).toISOString().replace('T',' ').replace(/\.\d{6}Z?$/, '') : '';
      case 'elapsedTime': return typeof log.elapsedTime === 'number' ? `${log.elapsedTime} secs` : '-';
      default: return '';
    }
  };
  const displayLogsRows = useMemo(() => {
    const list = (activeLogs && logsState[activeLogs]?.items) ? logsState[activeLogs].items : [];
    if (!showFiltersLogs) return list;
    const { columnKey, operator, value } = filterLogs;
    const v = String(value ?? '').toLowerCase();
    return list.filter((log, idx) => {
      const cell = String(getCellValueLogs(log, columnKey, idx) ?? '');
      const c = cell.toLowerCase();
      switch (operator) {
        case 'contains': return c.includes(v);
        case 'not_contains': return !c.includes(v);
        case 'equals': return c === v;
        case 'not_equals': return c !== v;
        case 'starts_with': return c.startsWith(v);
        case 'ends_with': return c.endsWith(v);
        case 'is_empty': return c.length === 0;
        case 'not_empty': return c.length > 0;
        case 'any_of': {
          const parts = v.split(',').map(s=>s.trim()).filter(Boolean);
          if (!parts.length) return true;
          return parts.includes(c);
        }
        default: return true;
      }
    });
  }, [activeLogs, logsState, showFiltersLogs, filterLogs]);

  // Logs table: density
  const [densityLogs, setDensityLogs] = useState('standard');
  const [densityAnchorLogs, setDensityAnchorLogs] = useState(null);
  const openDensityMenuLogs = Boolean(densityAnchorLogs);

  // Logs table: export
  const [exportAnchorLogs, setExportAnchorLogs] = useState(null);
  const openExportMenuLogs = Boolean(exportAnchorLogs);
  const visibleColumnDefsLogs = useMemo(() => allColumnsLogs.filter(c => isVisibleLogs(c.key)), [allColumnsLogs, visibleColsLogs]);
  const buildCsvLogs = () => {
    const headers = visibleColumnDefsLogs.map(c => c.label);
    const lines = [headers.join(',')];
    displayLogsRows.forEach((log, idx) => {
      const rowVals = visibleColumnDefsLogs.map((c) => {
        const raw = getCellValueLogs(log, c.key, idx);
        const val = String(raw ?? '').replace(/"/g, '""');
        return /[",\n]/.test(val) ? `"${val}"` : val;
      });
      lines.push(rowVals.join(','));
    });
    return lines.join('\n');
  };
  const handleDownloadCsvLogs = () => {
    const csv = buildCsvLogs();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'connection-logs.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportAnchorLogs(null);
  };
  const handlePrintLogs = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const tableHead = `<tr>${visibleColumnDefsLogs.map(c => `<th style="text-align:left; padding:8px; border-bottom:1px solid #e5e7eb;">${c.label}</th>`).join('')}</tr>`;
    const tableRows = displayLogsRows.map((log, idx) => `<tr>${visibleColumnDefsLogs.map(c => `<td style=\"padding:8px; border-bottom:1px solid #f1f5f9;\">${(getCellValueLogs(log, c.key, idx) || '')}</td>`).join('')}</tr>`).join('');
    win.document.write(`<!doctype html><html><head><title>Connection Logs</title><meta charset="utf-8" /><style>body{font-family:Inter,system-ui,Arial,sans-serif;padding:16px;} table{border-collapse:collapse;width:100%;} th{background:#f1f5f9;font-weight:600;}</style></head><body><h2>Connection Logs</h2><table>${tableHead}${tableRows}</table></body></html>`);
    win.document.close(); win.focus(); win.print(); win.close();
    setExportAnchorLogs(null);
  };

  // Export state and helpers
  const [exportAnchor, setExportAnchor] = useState(null);
  const openExportMenu = Boolean(exportAnchor);
  const visibleColumnDefs = useMemo(() => allColumns.filter(c => isVisible(c.key)), [allColumns, visibleCols]);
  const buildCsv = () => {
    const headers = visibleColumnDefs.map(c => c.label);
    const lines = [headers.join(',')];
    for (const r of displayRows) {
      const rowVals = visibleColumnDefs.map((c) => {
        const raw = getCellValue(r, c.key);
        // escape CSV value
        const val = String(raw ?? '').replace(/"/g, '""');
        return /[",\n]/.test(val) ? `"${val}"` : val;
      });
      lines.push(rowVals.join(','));
    }
    return lines.join('\n');
  };
  const handleDownloadCsv = () => {
    const csv = buildCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'connections.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  };
  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const tableHead = `<tr>${visibleColumnDefs.map(c => `<th style="text-align:left; padding:8px; border-bottom:1px solid #e5e7eb;">${c.label}</th>`).join('')}</tr>`;
    const tableRows = displayRows.map(r => `<tr>${visibleColumnDefs.map(c => `<td style=\"padding:8px; border-bottom:1px solid #f1f5f9;\">${(getCellValue(r, c.key) || '')}</td>`).join('')}</tr>`).join('');
    win.document.write(`<!doctype html><html><head><title>Connections</title><meta charset="utf-8" /><style>body{font-family:Inter,system-ui,Arial,sans-serif;padding:16px;} table{border-collapse:collapse;width:100%;} th{background:#f1f5f9;font-weight:600;}</style></head><body><h2>Connections</h2><table>${tableHead}${tableRows}</table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
    setExportAnchor(null);
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getDevopsConnections();
        if (!isMounted) return;
        const mapped = Array.isArray(data) ? data.map((d, i) => ({ id: i + 1, ...d })) : [];
        setRows(mapped);
      } catch (e) {
        if (!isMounted) return;
        setRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const link = await getAwxConnection();
        if (!mounted) return;
        setAwxLinked(!!link);
      } catch (e) {
        if (!mounted) return;
        setAwxLinked(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const total = rows.length;
  const active = rows.filter((r) => Number(r.isActive) === 1).length;
  const inactive = total - active;

  const handleAddSave = async (newItem) => {
    try {
      const resp = await postConfigureConnection(newItem);
      setSnack({ open: true, message: (resp && (resp.message || resp.msg)) || 'Connection configured successfully', severity: 'success' });
      // refresh list
      setLoading(true);
      const data = await getDevopsConnections();
      const mapped = Array.isArray(data) ? data.map((d, i) => ({ id: i + 1, ...d })) : [];
      setRows(mapped);
      setOpenAdd(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Failed to configure connection';
      setSnack({ open: true, message: apiMsg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleLogs = async (row) => {
    const connId = row.connectionId;
    const isSame = activeLogs === connId;
    if (isSame) {
      setActiveLogs(null);
      return;
    }
    setActiveLogs(connId);
    if (!logsState[connId] || !(logsState[connId].items && logsState[connId].items.length)) {
      setLogsState((s) => ({ ...s, [connId]: { ...(s[connId] || {}), loading: true, items: [] } }));
      try {
        const items = await fetchConnectionJobs(connId);
        setLogsState((s) => ({ ...s, [connId]: { ...(s[connId] || {}), loading: false, items } }));
      } catch (e) {
        setLogsState((s) => ({ ...s, [connId]: { ...(s[connId] || {}), loading: false, items: [] } }));
        const apiMsg = e?.message || 'Failed to fetch logs';
        setSnack({ open: true, message: apiMsg, severity: 'error' });
      }
    }
  };

  const handleAwxSetup = async () => {
    try {
      setAwxSubmitting(true);
      const res = await awxAccountSetup();
      setSnack({ open: true, message: (res && (res.message || res.msg)) || 'AWX account linked successfully', severity: 'success' });
      setAwxLinked(true);
      setOpenAwx(false);
      setOpenAdd(true);
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Failed to setup AWX account';
      setSnack({ open: true, message: apiMsg, severity: 'error' });
    } finally {
      setAwxSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1250, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Setup Infrastructure
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            if (awxLinked === false) { setOpenAwx(true); return; }
            setOpenAdd(true);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#1E88E5',
            '&:hover': { bgcolor: '#1565C0' },
            width: { xs: '100%', md: 'auto' },
            alignSelf: { xs: 'stretch', md: 'auto' },
            px: 2.5,
            minWidth: { md: 220 },
          }}
          startIcon={<AddLink />}
          disabled={awxLinked === false && !openAwx}
        >
          New Connections
        </Button>
      </Stack>

      {awxLinked === false && (
        <Paper sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#FFF8E1' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Typography variant="body2" sx={{ color: '#6B6F76' }}>
              AWX account is not linked. Please complete AWX Account Setup to add infrastructure connections.
            </Typography>
            <Button variant="contained" size="small" onClick={() => setOpenAwx(true)} sx={{ textTransform: 'none' }}>Setup AWX</Button>
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <StatCard title="Total List" value={total} icon={<Storage />} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard title="Active Connections" value={active} color="#43BA9F" bg="#E8F8F3" icon={<Dns />} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard title="Inactive Connections" value={inactive} color="#BA4345" bg="#FCEBED" icon={<Sensors />} />
        </Box>
      </Stack>

      <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', position: 'relative' }}>
        <Box sx={{ bgcolor: '#506C84', color: 'white', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Connections</Typography>
          <Stack direction="row" spacing={1} sx={{ '& .MuiButton-root': { color: 'white', borderColor: 'rgba(255,255,255,0.35)' } }}>
            <Button
              size="small"
              startIcon={<ViewColumn />}
              variant="outlined"
              onClick={(e) => setColMenuAnchor(e.currentTarget)}
            >
              Columns
            </Button>
            <Button size="small" startIcon={<FilterList />} variant="outlined" onClick={() => setShowFilters((s) => !s)}>Filters</Button>
            <Button size="small" startIcon={<DensitySmall />} variant="outlined" onClick={(e)=>setDensityAnchor(e.currentTarget)}>Density</Button>
            <Button size="small" startIcon={<IosShare />} variant="outlined" onClick={(e)=>setExportAnchor(e.currentTarget)}>Export</Button>
          </Stack>
        </Box>
        <Menu
          anchorEl={colMenuAnchor}
          open={openColMenu}
          onClose={() => setColMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 260, p: 1 } }}
        >
          <Box sx={{ px: 1, pb: 1 }}>
            <TextField
              size="small"
              placeholder="Search"
              fullWidth
              value={colSearch}
              onChange={(e) => setColSearch(e.target.value)}
            />
          </Box>
          <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {filteredCols.map((c) => (
              <ListItem key={c.key} disableGutters secondaryAction={null} onClick={() => toggleCol(c.key)} sx={{ px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox edge="start" checked={isVisible(c.key)} tabIndex={-1} disableRipple size="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={c.label} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
            <Button size="small" color="inherit" onClick={resetCols}>Reset</Button>
          </Box>
        </Menu>
        <Menu
          anchorEl={exportAnchor}
          open={openExportMenu}
          onClose={() => setExportAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 220, p: 0.5 } }}
        >
          <List dense disablePadding>
            <ListItem disableGutters onClick={handleDownloadCsv} sx={{ px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={'Download as CSV'} />
            </ListItem>
            <ListItem disableGutters onClick={handlePrint} sx={{ px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={'Print'} />
            </ListItem>
          </List>
        </Menu>
        <Menu
          anchorEl={densityAnchor}
          open={openDensityMenu}
          onClose={() => setDensityAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 220, p: 0.5 } }}
        >
          <List dense disablePadding>
            {[
              { key: 'compact', label: 'Compact' },
              { key: 'standard', label: 'Standard' },
              { key: 'comfortable', label: 'Comfortable' },
            ].map((opt) => (
              <ListItem
                key={opt.key}
                disableGutters
                onClick={() => { setDensity(opt.key); setDensityAnchor(null); }}
                sx={{ px: 1, py: 0.25, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  <DensitySmall fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={opt.label} />
                {density === opt.key && <Check fontSize="small" sx={{ ml: 1 }} />}
              </ListItem>
            ))}
          </List>
        </Menu>
        {showFilters && (
          <Box sx={{ position: 'absolute', zIndex: 10, left: { xs: 8, md: 16 }, right: { xs: 8, md: 16 }, top: 56 }}>
            <Paper elevation={3} sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={3.5}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Columns</Typography>
                  <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <Select
                      value={filter.columnKey}
                      onChange={(e) => setFilter((f) => ({ ...f, columnKey: e.target.value }))}
                    >
                      {allColumns.map((c) => (
                        <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3.5}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Operator</Typography>
                  <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <Select
                      value={filter.operator}
                      onChange={(e) => setFilter((f) => ({ ...f, operator: e.target.value }))}
                    >
                      {filterOperators.map((op) => (
                        <MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Value</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={filter.operator === 'any_of' ? 'Comma separated values' : 'Filter value'}
                    value={filter.value}
                    onChange={(e) => setFilter((f) => ({ ...f, value: e.target.value }))}
                    disabled={filter.operator === 'is_empty' || filter.operator === 'not_empty'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: { xs: 'space-between', md: 'flex-end' }, alignItems: 'center' }}>
                  <Button size="small" color="inherit" onClick={() => setFilter({ columnKey: 'sno', operator: 'contains', value: '' })}>Clear</Button>
                  <IconButton size="small" onClick={() => setShowFilters(false)} aria-label="Close filters" sx={{ ml: 0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
        <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'auto' }}>
          <Table size="small" stickyHeader sx={{
            '& .MuiTableCell-root': {
              py: density === 'compact' ? 0.5 : density === 'comfortable' ? 1.75 : 1.0,
            },
          }}>
            <TableHead>
              <TableRow>
                {isVisible('sno') && <TableCell align="center">S.no</TableCell>}
                {isVisible('name') && <TableCell align="center">Name</TableCell>}
                {isVisible('type') && <TableCell align="center">Type</TableCell>}
                {isVisible('infra') && <TableCell align="center">Infra</TableCell>}
                {isVisible('hostname') && <TableCell align="center">Hostname</TableCell>}
                {isVisible('username') && <TableCell align="center">Username</TableCell>}
                {isVisible('createdBy') && <TableCell align="center">Created By</TableCell>}
                {isVisible('status') && <TableCell align="center">Status</TableCell>}
                {isVisible('createdOn') && <TableCell align="center">Created On</TableCell>}
                {isVisible('port') && <TableCell align="center">Port</TableCell>}
                {isVisible('logs') && <TableCell align="center">Logs</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& tr:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.015)' } }}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">Loading...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">No connections found</TableCell>
                </TableRow>
              ) : (
                displayRows.map((row) => (
                  <React.Fragment key={row.connectionId}>
                    <TableRow hover>
                      {isVisible('sno') && (
                        <TableCell align="center">{row.id}</TableCell>
                      )}
                      {isVisible('name') && (
                        <TableCell align="center" sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={row.connectionName}>{row.connectionName}</Typography>
                        </TableCell>
                      )}
                      {isVisible('type') && (
                        <TableCell align="center">{row.connectionType}</TableCell>
                      )}
                      {isVisible('infra') && (
                        <TableCell align="center">{row.infraType}</TableCell>
                      )}
                      {isVisible('hostname') && (
                        <TableCell align="center" sx={{ maxWidth: 160 }}>
                          <Typography variant="body2" noWrap title={row.hostName}>{row.hostName}</Typography>
                        </TableCell>
                      )}
                      {isVisible('username') && (
                        <TableCell align="center">{row.username}</TableCell>
                      )}
                      {isVisible('createdBy') && (
                        <TableCell align="center">{row.createdBy}</TableCell>
                      )}
                      {isVisible('status') && (
                        <TableCell align="center">
                          <Chip size="small" label={Number(row.isActive) === 1 ? 'Active' : 'Inactive'}
                            sx={{ bgcolor: Number(row.isActive) === 1 ? '#CFF2E9' : '#F6E2E9', color: Number(row.isActive) === 1 ? '#43BA9F' : '#BA4345', fontWeight: 600, borderRadius: '16px' }}
                          />
                        </TableCell>
                      )}
                      {isVisible('createdOn') && (
                        <TableCell align="center">{row.createdOn ? new Date(row.createdOn).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC') : ''}</TableCell>
                      )}
                      {isVisible('port') && (
                        <TableCell align="center">{row.port || 'NA'}</TableCell>
                      )}
                      {isVisible('logs') && (
                        <TableCell align="center">
                          <Tooltip title="View Logs">
                            <span>
                              <IconButton size="small" onClick={() => toggleLogs(row)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                    
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {activeLogs && (
        <Paper sx={{ p: 0, mt: 2, overflow: 'hidden', borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', position: 'relative' }}>
          <Box sx={{ bgcolor: '#506C84', color: 'white', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>Logs</Typography>
            <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: 'flex-end', '& .MuiButton-root': { color: 'white', borderColor: 'rgba(255,255,255,0.35)' } }}>
              <Button size="small" startIcon={<ViewColumn />} variant="outlined" onClick={(e)=>setColMenuAnchorLogs(e.currentTarget)}>Columns</Button>
              <Button size="small" startIcon={<FilterList />} variant="outlined" onClick={() => setShowFiltersLogs((s)=>!s)}>Filters</Button>
              <Button size="small" startIcon={<DensitySmall />} variant="outlined" onClick={(e)=>setDensityAnchorLogs(e.currentTarget)}>Density</Button>
              <Button size="small" startIcon={<IosShare />} variant="outlined" onClick={(e)=>setExportAnchorLogs(e.currentTarget)}>Export</Button>
            </Stack>
            <IconButton size="small" onClick={() => setActiveLogs(null)} sx={{ color: 'white' }} aria-label="Close logs">
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Menu
            anchorEl={colMenuAnchorLogs}
            open={openColMenuLogs}
            onClose={() => setColMenuAnchorLogs(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 260, p: 1 } }}
          >
            <Box sx={{ px: 1, pb: 1 }}>
              <TextField size="small" placeholder="Search" fullWidth value={colSearchLogs} onChange={(e)=>setColSearchLogs(e.target.value)} />
            </Box>
            <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {filteredColsLogs.map((c) => (
                <ListItem key={c.key} disableGutters onClick={() => toggleColLogs(c.key)} sx={{ px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Checkbox edge="start" checked={isVisibleLogs(c.key)} tabIndex={-1} disableRipple size="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={c.label} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
              <Button size="small" color="inherit" onClick={resetColsLogs}>Reset</Button>
            </Box>
          </Menu>
          <Menu
            anchorEl={exportAnchorLogs}
            open={openExportMenuLogs}
            onClose={() => setExportAnchorLogs(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 220, p: 0.5 } }}
          >
            <List dense disablePadding>
              <ListItem disableGutters onClick={handleDownloadCsvLogs} sx={{ px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={'Download as CSV'} />
              </ListItem>
              <ListItem disableGutters onClick={handlePrintLogs} sx={{ px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={'Print'} />
              </ListItem>
            </List>
          </Menu>
          <Menu
            anchorEl={densityAnchorLogs}
            open={openDensityMenuLogs}
            onClose={() => setDensityAnchorLogs(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 220, p: 0.5 } }}
          >
            <List dense disablePadding>
              {[
                { key: 'compact', label: 'Compact' },
                { key: 'standard', label: 'Standard' },
                { key: 'comfortable', label: 'Comfortable' },
              ].map((opt) => (
                <ListItem key={opt.key} disableGutters onClick={() => { setDensityLogs(opt.key); setDensityAnchorLogs(null); }} sx={{ px: 1, py: 0.25, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    <DensitySmall fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={opt.label} />
                  {densityLogs === opt.key && <Check fontSize="small" sx={{ ml: 1 }} />}
                </ListItem>
              ))}
            </List>
          </Menu>
          {showFiltersLogs && (
            <Box sx={{ position: 'absolute', zIndex: 10, left: { xs: 8, md: 16 }, right: { xs: 8, md: 16 }, top: 56 }}>
              <Paper elevation={3} sx={{ p: { xs: 1.0, md: 1.25 }, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={3.5}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Columns</Typography>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                      <Select
                        value={filterLogs.columnKey}
                        onChange={(e) => setFilterLogs((f) => ({ ...f, columnKey: e.target.value }))}
                      >
                        {allColumnsLogs.map((c) => (
                          <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3.5}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Operator</Typography>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                      <Select
                        value={filterLogs.operator}
                        onChange={(e) => setFilterLogs((f) => ({ ...f, operator: e.target.value }))}
                      >
                        {filterOperators.map((op) => (
                          <MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Value</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={filterLogs.operator === 'any_of' ? 'Comma separated values' : 'Filter value'}
                      value={filterLogs.value}
                      onChange={(e) => setFilterLogs((f) => ({ ...f, value: e.target.value }))}
                      disabled={filterLogs.operator === 'is_empty' || filterLogs.operator === 'not_empty'}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button size="small" color="inherit" onClick={() => setFilterLogs({ columnKey: 'name', operator: 'contains', value: '' })}>Clear</Button>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
          <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'auto' }}>
            {logsState[activeLogs]?.loading ? (
              <Typography variant="body2">Loading logs...</Typography>
            ) : ((displayLogsRows?.length || 0) > 0 ? (
              <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: densityLogs === 'compact' ? 0.5 : densityLogs === 'comfortable' ? 1.75 : 1.0 } }}>
                <TableHead>
                  <TableRow>
                    {isVisibleLogs('sno') && <TableCell align="center">S. no</TableCell>}
                    {isVisibleLogs('name') && <TableCell align="center">Name</TableCell>}
                    {isVisibleLogs('description') && <TableCell align="center">Description</TableCell>}
                    {isVisibleLogs('status') && <TableCell align="center">Status</TableCell>}
                    {isVisibleLogs('type') && <TableCell align="center">Type</TableCell>}
                    {isVisibleLogs('createdAt') && <TableCell align="center">Created On</TableCell>}
                    {isVisibleLogs('finishedAt') && <TableCell align="center">Completed On</TableCell>}
                    {isVisibleLogs('elapsedTime') && <TableCell align="center">Duration</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayLogsRows.map((log, idx) => (
                    <TableRow key={log.jobId || idx}>
                      {isVisibleLogs('sno') && (
                        <TableCell align="center">{idx + 1}</TableCell>
                      )}
                      {isVisibleLogs('name') && (
                        <TableCell align="center" sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={log.name}>{log.name}</Typography>
                        </TableCell>
                      )}
                      {isVisibleLogs('description') && (
                        <TableCell align="center" sx={{ maxWidth: 260 }}>
                          <Typography variant="body2" noWrap title={log.description || ''}>{log.description || '-'}</Typography>
                        </TableCell>
                      )}
                      {isVisibleLogs('status') && (
                        <TableCell align="center">
                          <Chip size="small" label={log.status || '-'}
                            sx={{
                              bgcolor: (log.status || '').toLowerCase() === 'successful' ? '#CFF2E9' : '#FCEBED',
                              color: (log.status || '').toLowerCase() === 'successful' ? '#43BA9F' : '#BA4345',
                              fontWeight: 600, borderRadius: '16px'
                            }}
                          />
                        </TableCell>
                      )}
                      {isVisibleLogs('type') && (
                        <TableCell align="center">{log.type || '-'}</TableCell>
                      )}
                      {isVisibleLogs('createdAt') && (
                        <TableCell align="center">{log.createdAt ? new Date(log.createdAt).toISOString().replace('T', ' ').replace(/\.\d{6}Z?$/, '') : '-'}</TableCell>
                      )}
                      {isVisibleLogs('finishedAt') && (
                        <TableCell align="center">{log.finishedAt ? new Date(log.finishedAt).toISOString().replace('T', ' ').replace(/\.\d{6}Z?$/, '') : '-'}</TableCell>
                      )}
                      {isVisibleLogs('elapsedTime') && (
                        <TableCell align="center">{typeof log.elapsedTime === 'number' ? `${log.elapsedTime} secs` : '-'}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2">No logs found</Typography>
            ))}
          </Box>
        </Paper>
      )}

      <AddConnectionModal open={openAdd} onClose={() => setOpenAdd(false)} onSave={handleAddSave} />
      <AwxSetupModal open={openAwx} onClose={() => setOpenAwx(false)} onSetup={handleAwxSetup} loading={awxSubmitting} />
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetupInfrastructure;
